import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { fetchAllPages } from './fetch-all-pages';
import type { InstructorAttempt, GradeAnswerInput, InstructorAttemptsParams } from '@/types';
import type { CorrectiveAction } from '@/types';

const KEYS = {
  attempts: (params?: InstructorAttemptsParams) => ['instructor', 'attempts', params] as const,
  correctiveActions: ['instructor', 'corrective-actions'] as const,
};

export function useInstructorAttempts(params?: InstructorAttemptsParams) {
  return useQuery({
    queryKey: KEYS.attempts(params),
    queryFn: () =>
      api.get<InstructorAttempt[]>('/quizzes/instructor-attempts', {
        status: params?.status,
        page: params?.page,
        per_page: params?.per_page,
      }),
    placeholderData: keepPreviousData,
  });
}

export function useGradeAnswer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ answerId, data }: { answerId: number; data: GradeAnswerInput }) =>
      api.patch(`/quiz-answers/${answerId}/grade`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['instructor', 'attempts'] });
    },
  });
}

export function useInstructorCorrectiveActions() {
  return useQuery({
    queryKey: KEYS.correctiveActions,
    // El API limita per_page a 100 en acciones correctivas.
    queryFn: () =>
      fetchAllPages<CorrectiveAction>('/corrective-actions', undefined, { pageSize: 100 }),
    select: (res) => res.data,
  });
}

export function useCreateCorrectiveAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      user_id: number;
      trigger_type: string;
      description: string;
      required_actions: string;
      due_date?: string;
    }) => api.post<CorrectiveAction>('/corrective-actions', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.correctiveActions });
      qc.invalidateQueries({ queryKey: ['competency', 'corrective-actions'] });
    },
  });
}
